/**
 * 角色关系状态类型定义
 *
 * 定义玩家与某个角色之间的"关系"，作为剧情维度的独立状态。
 *
 * 设计目标：
 * - 把 `relation` 从 `CharacterProfile` 上剥离，让人物本身不再带"出厂阵营"烙印。
 * - 关系是动态的、有历史可溯的：
 *   "魔王不一定永远是敌人，队友也不一定永远是队友"。
 * - 同一个角色对不同 player 视角可以有不同关系（多 player 场景预留）。
 *
 * 数据组织：
 *   Map<playerId, Map<characterId, RelationState>>
 *
 * 旧存档中的 profile relation 会由迁移层一次性转入本状态。
 */
import { z } from 'zod';

// ============================================
// 关系立场
// ============================================

/**
 * 关系立场
 *
 * - friendly：友好（可能是同伴、盟友、亲人）
 * - neutral：中立（认识但无明显倾向）
 * - hostile：敌对（当前会主动攻击玩家）
 * - unknown：陌生（玩家尚未与该角色建立有效互动）
 *
 * unknown 是缺省值，比 neutral 更明确："没建立过关系"与"建立了但中立"是不同语义。
 */
export const RelationStanceSchema = z.enum(['friendly', 'neutral', 'hostile', 'unknown']);
export type RelationStance = z.infer<typeof RelationStanceSchema>;

// ============================================
// 关系事件
// ============================================

/**
 * 关系变化事件
 *
 * 用于回溯关系如何演变到当前状态。
 * 适用场景：招募系统的对话提示、剧情回顾、好感度变化日志。
 */
export const RelationEventSchema = z.object({
  /** 事件发生的游戏内时间戳 */
  at: z.number(),
  /** 事件类型（具体取值由调用方约定，留作开放枚举） */
  type: z.string().min(1),
  /** 事件简述（用于显示） */
  summary: z.string().default(''),
  /** 关系立场变化（before → after，可选） */
  stanceChange: z
    .object({
      from: RelationStanceSchema,
      to: RelationStanceSchema,
    })
    .optional(),
  /** 好感度变化（可选） */
  affinityDelta: z.number().optional(),
  /** 信任度变化（可选） */
  trustDelta: z.number().optional(),
});
export type RelationEvent = z.infer<typeof RelationEventSchema>;

// ============================================
// 关系状态
// ============================================

/**
 * 玩家与某个角色之间的关系快照
 */
export const RelationStateSchema = z.object({
  /** 当前立场 */
  stance: RelationStanceSchema.default('unknown'),
  /**
   * 好感度
   *
   * 当前阶段约定：
   * - 该值主要表达“在天然关系基线之上的动态增量”
   * - 例如哥布林天生对人类 -55，若玩家因装备/剧情获得 +30，则最终 affinity = -25
   *
   * 区间 -100 ~ +100：
   * - +60 以上：可能愿意加入或主动协助
   * - 0 ~ +60：友好但保留判断
   * - -60 ~ 0：警惕但未敌对
   * - -60 以下：明确反感，倾向于阻碍
   */
  affinity: z.number().min(-100).max(100).default(0),
  /**
   * 信任度
   *
   * 区间 0 ~ 100：affinity 的"是否愿意把背后交给玩家"维度。
   * 高 affinity + 低 trust = 喜欢但不信任（典型如刚认识的暧昧对象）。
   */
  trust: z.number().min(0).max(100).default(0),
  /** 玩家初次认识该角色的游戏内时间戳 */
  knownSince: z.number().optional(),
  /** 关系变化事件列表 */
  events: z.array(RelationEventSchema).default([]),
});
export type RelationState = z.infer<typeof RelationStateSchema>;

/**
 * 默认关系状态（陌生人）
 */
export const DEFAULT_RELATION_STATE: RelationState = RelationStateSchema.parse({});

// ============================================
// 持久化 Schema（GameState 序列化用）
// ============================================

/**
 * 关系 store 的持久化形状：characterId → RelationState
 *
 * 当前游戏假定单玩家概念，所以序列化时只存一层 Map。
 * 未来需要支持多玩家时，可在外层再嵌套一层 playerId → 这个 record。
 */
export const CharacterRelationsPersistenceStateSchema = z.record(z.string(), RelationStateSchema);
export type CharacterRelationsPersistenceState = z.infer<typeof CharacterRelationsPersistenceStateSchema>;
